import { Card, CardContent, DataTable, MetricCard, PageHeader } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { usePlatformOverview } from "../../features/platform/queries.ts";
import type { PlatformAuditEntry } from "../../features/platform/types.ts";

export const Route = createFileRoute("/_app/dashboard")({ component: DashboardScreen });

function DashboardScreen() {
  const { data, isLoading, error } = usePlatformOverview();
  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform Overview"
        subtitle="Cross-tenant aggregates only — no individual user data (FR-SADMIN-016)."
      />
      {error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {error.message}
        </p>
      ) : null}
      {isLoading ? <p className="text-muted-foreground text-sm">Loading…</p> : null}
      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Venues"
              value={data.venues.total}
              caption={`${data.venues.active} active · ${data.venues.suspended} suspended`}
              tone={data.venues.suspended ? "warning" : "neutral"}
            />
            <MetricCard label="Branches" value={data.branchesTotal} />
            <MetricCard label="Staff accounts" value={data.staffTotal} />
            <MetricCard
              label="Open sessions"
              value={data.openSessions}
              caption={`${data.activeCheckIns} active check-ins`}
              tone={data.openSessions ? "info" : "neutral"}
            />
            <MetricCard label="Orders (all time)" value={data.ordersTotal} />
            <MetricCard label="Gifts (all time)" value={data.giftsTotal} />
            <MetricCard label="Screen posts" value={data.screenPostsTotal} />
          </div>
          <Card>
            <CardContent className="space-y-4 p-5">
              <h2 className="font-display font-semibold text-lg">Recent platform activity</h2>
              <DataTable<PlatformAuditEntry>
                rows={data.recentActivity}
                getRowKey={(row) => row.id}
                empty={<p className="text-muted-foreground text-sm">No platform activity yet.</p>}
                columns={[
                  { key: "action", header: "Action", cell: (row) => row.action },
                  { key: "venue", header: "Venue", cell: (row) => row.venueName ?? "—" },
                  { key: "actor", header: "Actor", cell: (row) => row.actorType },
                  {
                    key: "at",
                    header: "When",
                    cell: (row) => new Date(row.createdAt).toLocaleString(),
                  },
                ]}
              />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
